import React, { useState, useEffect, useContext } from "react";
import { MessageBox } from "react-chat-elements";
import "react-chat-elements/dist/main.css";
import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import { ApiContext } from '../../Contexts/ApiContext';
import MarkdownTextPresenter from '../MarkdownTextPresenter';
import { useApplication } from '../../Contexts/ApplicationContext';
import { useNavigate } from "react-router-dom";


const TranscriptionComponent = () => {
    const [isRecording, setIsRecording] = React.useState(false);
    const [isTranscripting, setIsTranscripting] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState('Transcript');
    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);
    const [summary, setSummary] = useState("");
    const [caseNotes, setCaseNotes] = useState("");
    const conversationTranscriberRef = React.useRef(null);
    const [sessionDate, setSessionDate] = useState(new Date());
    const [currentTranscriptionId, setCurrentTranscriptionId] = useState(null);
    const [transcriptions, setTranscriptions] = useState([]);
    const [currentTranscription,setCurrentTranscription] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [timerInterval, setTimerInterval] = useState(null);
    const [showConfirmClose, setShowConfirmClose] = useState(false);
    const [showConfirmStop, setShowConfirmStop] = useState(false);
    const [patientReference, setPatientReference] = useState("");
    const [referenceError, setReferenceError] = useState("");
    const [modalFlowStep, setModalFlowStep] = useState("");
    const [speechTokenRefreshCounter, setSpeechTokenRefreshCounter] = useState(0);
    const [speechSessionIds, setSpeechSessionIds] = useState([]);

    const [sessionStopped, setSessionStopped] = useState(false);
    const [consentChecked, setConsentChecked] = useState(false);


    const {
        getCognitiveServicesTokenAndRegionAsync,
        postTranscriptionAsync,
        getSummaryAsync,
        getCaseNotesAsync
    } = useContext(ApiContext);

    const { setIsTranscribing } = useApplication();
    // Add new state for tracking selected device in modal
    const [tempSelectedDevice, setTempSelectedDevice] = useState(null);

    useEffect(() => {
        if (setIsTranscribing) {  // Add a check to ensure the prop exists
            setIsTranscribing(isTranscripting);
        }
    }, [isTranscripting, setIsTranscribing]);

    const generateSummaryOpenAI = async () => {
        const summery = await getSummaryAsync(currentTranscriptionId);
        setSummary(summery);
    };

    const generateCaseNotesOpenAI = async () => {
        const notes = await getCaseNotesAsync(currentTranscriptionId);
        setCaseNotes(notes);
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleStartRecording = () => {

        setConsentChecked(false);

        const savedDeviceId = localStorage.getItem("aiSelectedDevice");
        if (!selectedDevice) {

            const getAudioDevices = async () => {
                try {
                    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
                        console.error("MediaDevices API not supported.");
                        return;
                    }

                    await navigator.mediaDevices.getUserMedia({ audio: true });

                    const devices = await navigator.mediaDevices.enumerateDevices();
                    const audioInputDevices = devices.filter((device) => device.kind === "audioinput");

                    setDevices(audioInputDevices);
                    if(savedDeviceId){
                        const defualtDevice = devices.find(x=> x.deviceId == savedDeviceId);
                        if(defualtDevice)
                            setTempSelectedDevice(defualtDevice);
                    }

                } catch (error) {
                    console.error("Error accessing audio devices:", error);
                }
            };

            setPatientReference(null);
            getAudioDevices();
            setIsReferenceModalOpen(true);

            
            return;
        }
        else {
            setIsTranscripting(!isTranscripting);
        }
    };

    // Modify handleDeviceSelect to only store the selection temporarily
    const handleDeviceSelect = (device) => {
        setTempSelectedDevice(device);
    };

    // Add new function to confirm device selection
    const confirmDeviceSelection = () => {
        if (tempSelectedDevice) {
            setSelectedDevice(tempSelectedDevice);
            localStorage.setItem("aiSelectedDevice", tempSelectedDevice.deviceId);
            setIsModalOpen(false);
            setIsTranscripting(!isTranscripting);
            //startTranscription();
            setActiveTab('Transcript')
        }
    };

    const confirmReferenceSelection = () => {
        if (patientReference) {
            setIsReferenceModalOpen(false);
            setPatientReference(patientReference);
            setIsModalOpen(true);
        }
    };

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (isTranscripting) {
                event.preventDefault();
                event.returnValue = "Are you sure you want to leave? Ongoing transcription will be lost.";
                return event.returnValue;
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
        
    }, [isTranscripting]);

    const navigate = useNavigate();


    useEffect(() => {
        console.log("loaded");

        const preventBackNavigation = () => {
            window.history.pushState(null, "", window.location.href);
        };
        window.history.pushState(null, "", window.location.href);
        window.addEventListener("popstate", preventBackNavigation);

        return () => {
            conversationTranscriberRef &&
                conversationTranscriberRef.current &&
                conversationTranscriberRef.current.stopTranscribingAsync(() => {
                    console.log("Recognition stopped.");
                    conversationTranscriberRef.current.close(); 
                    conversationTranscriberRef.current = null;
                }, (err) => {
                    console.error("Error stopping recognition:", err);
                });

            console.log("TranscriptionComponent is being unloaded."); // Add any additional cleanup logic here
        };
    }, []);

    const handleStopSession = async () => {
        setShowConfirmStop(true);
    }

    const startTranscription = async () => {
        if (isRecording) return;

        setActiveTab('Transcript');

        setIsLoading(true);
        setIsRecording(!isRecording);
        setSummary("");
        setCaseNotes("");

        const { accessToken, region } = await getCognitiveServicesTokenAndRegionAsync();
        const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(accessToken, region);

        speechConfig.speechRecognitionLanguage = process.env.REACT_APP_SPEECH_LANGUAGE_REGION;

        console.log("REACT_APP_SPEECH_LANGUAGE_REGION: ", process.env.REACT_APP_SPEECH_LANGUAGE_REGION);

        if (process.env.REACT_APP_ENABLE_AUDIO_LOGGING) {
            console.log("Logs enabled");
            speechConfig.enableAudioLogging();
        }

        const audioConfig = sdk.AudioConfig.fromMicrophoneInput(selectedDevice);

        const conversationTranscriber = new sdk.ConversationTranscriber(speechConfig, audioConfig);

        conversationTranscriberRef.current = conversationTranscriber;

        conversationTranscriber.sessionStarted = function (s, e) {
            console.log("SessionStarted event");
            console.log("Transcription e: ", e);
            setSpeechSessionIds(prev => [...prev, e.sessionId]);
        };

        conversationTranscriber.sessionStopped = function (s, e) {
            console.log("SessionStopped event");
            conversationTranscriber.stopTranscribingAsync();
            //setIsLoading(false);
        };

        conversationTranscriber.canceled = function (s, e) {
            console.log("Canceled event");
            conversationTranscriber.stopTranscribingAsync();
        };


        conversationTranscriber.transcribing = (s, e) => {
            if (e.result.text)
                setTranscriptions(prevTranscriptions => [
                    ...prevTranscriptions.filter(t => !t.isInterim),
                    { text: e.result.text, speakerId: e.result.speakerId, isInterim: true }]);
        };

        conversationTranscriber.transcribed = (s, e) => {
            if (e.result.text) 
                setTranscriptions(oldVal => {
                    const filteredOldVal = oldVal.filter(t => !t.isInterim);
                    if(filteredOldVal.length === 0) {
                        return [{ text: e.result.text, speakerId: e.result.speakerId, isInterim: false }];
                    }
                    const lastFilteredVal = filteredOldVal[filteredOldVal.length - 1];
                    if(lastFilteredVal.speakerId === e.result.speakerId) {
                        return [...filteredOldVal.slice(0, -1), { text: lastFilteredVal.text + " " + e.result.text, speakerId: e.result.speakerId, isInterim: false }];
                    }
                    else{
                        return [...filteredOldVal, { text: e.result.text, speakerId: e.result.speakerId, isInterim: false }];
                    }
                });
        };

        // Start conversation transcription
        conversationTranscriber.startTranscribingAsync(
            function () {
                console.log("ConversationTranscriber started");
                setIsRecording(!isRecording);
                setIsLoading(false);
                const interval = setInterval(() => {
                    setRecordingTime(prev => prev + 1);

                    setSpeechTokenRefreshCounter(prev => {
                        console.log("Speech token refresh counter: ", prev, " from ", process.env.REACT_APP_REFRESH_SPEECH_TOKEN_MINUTES * 60);
                        if (prev > process.env.REACT_APP_REFRESH_SPEECH_TOKEN_MINUTES * 60) {
                            getCognitiveServicesTokenAndRegionAsync()
                                .then(({ accessToken }) => {
                                    conversationTranscriberRef.current.authorizationToken = accessToken;
                                    console.log("Speech token refreshed: ", accessToken);
                                });
                            return 0;
                        }
                        return prev + 1;
                    });
                }, 1000);
                setTimerInterval(interval);
            },
            function (err) {
                console.trace("err - starting transcription: " + err);
                setIsLoading(false);
            }
        );
    };

    const stopTranscription = async () => {

        if (!isRecording || !conversationTranscriberRef.current) return;

        // Clear the timer interval
        if (timerInterval) {
            clearInterval(timerInterval);
            setTimerInterval(null);
        }

        // Clean up all interim transcriptions
        setTranscriptions(prevTranscriptions =>
            prevTranscriptions.filter(t => !t.isInterim)
        );

        setIsLoading(true);
        await conversationTranscriberRef.current.stopTranscribingAsync(() => {
            console.log("Recognition stopped.");
            conversationTranscriberRef.current.close(); // Cleanup
            conversationTranscriberRef.current = null;
            setIsRecording(false);
            
        }, (err) => {
            console.error("Error stopping recognition:", err);
            setIsLoading(false);
        });
        
        const transcription = transcriptionsToText();
        if (transcription.length > 0) {
            const transcriptionId = await postTranscriptionAsync(transcription, currentTranscriptionId, patientReference, speechSessionIds);

            if (transcriptionId) {
                setCurrentTranscriptionId(transcriptionId);
            }

            setCurrentTranscription(transcription);

        }
        else {
            console.log("Empty transcription. Nothing saved. Here should be nice modal or something");
        }

        setIsLoading(false);
    };

    const resetState = () => {
        // Clear the timer interval
        if (timerInterval) {
            clearInterval(timerInterval);
            setTimerInterval(null);
        }
        setSpeechSessionIds([]);
        setRecordingTime(0);
        setIsRecording(false);
        setSessionStopped(false);
        setIsTranscripting(false);
        setSelectedDevice(null);
        setIsModalOpen(false);
        setIsReferenceModalOpen(false);
        setSummary("");
        setCaseNotes("");
        setTranscriptions([]);
        setCurrentTranscriptionId(null);
        setIsLoading(false);
        
        console.log("State cleared");
    };

    const handleClose = () => {
        // Validate patient reference before showing confirmation
        if (!patientReference.trim()) {
            setReferenceError("Patient reference number is required");
            return;
        }
        setShowConfirmClose(true);
    };

    const confirmClose = async () => {

        if (currentTranscription.length > 0 && patientReference.trim().length > 0) {
            const transcriptionId = await postTranscriptionAsync(currentTranscription, currentTranscriptionId, patientReference, speechSessionIds);
        }
        else{
            console.log("On close triggered. Not sending anything as transcription is empty");
        }
        
        resetState();
        setShowConfirmClose(false);
        setPatientReference(""); // Reset the reference number
        setReferenceError(""); // Reset any errors
    };

    const confirmStop = async () => {
        await stopTranscription();
        setSessionStopped(true);
        setShowConfirmStop(false);
    }

    const transcriptionsToText = () => {
        const concatenatedText = transcriptions
            .map(transcription => transcription.speakerId + ": " + transcription.text) // Extract the `text` field from each transcription
            .join("\n");

        return concatenatedText;
    }

    return (
        <div className="flex flex-col h-full ">
            
            {isLoading ? (
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    <span className="ml-3">Loading...</span>
                </div>
            ) : (
                isTranscripting && (
                    <div className="flex flex-col h-full">
                        <div className="flex justify-between p-4 items-center">
                            <div className="flex gap-4 items-center">
                                <div className="text-xl font-mono bg-gray-100 px-4 py-2 rounded-lg">
                                    {formatTime(recordingTime)}
                                </div>
                                {isRecording && activeTab === 'Transcript' &&
                                    <button onClick={stopTranscription} className="w-28 bg-blue-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300">
                                        Pause
                                    </button>
                                }
                               
                                {!isRecording && !sessionStopped &&
                                    <button onClick={startTranscription} className="w-28 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300">
                                        {transcriptions.length > 0 ? "Continue" : "Start"}
                                    </button>
                                }
                                 {!sessionStopped &&
                                    <button onClick={handleStopSession} className="w-38 bg-yellow-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300">
                                        Stop Session
                                    </button>
                                 }
                                {!isRecording && 
                                    <button onClick={handleClose} className="w-38 bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300">
                                        Close Session
                                    </button>
                                 }
                            </div>
                            <div className="text-lg font-semibold">
                                Session date: {sessionDate.toLocaleString()}
                            </div>
                        </div>
                        <div className="flex-1 flex flex-col">
                            <div className="border-b border-gray-200">
                                <nav className="flex space-x-8 px-4" aria-label="Tabs">
                                    {isRecording ? (
                                        <button
                                            key="Transcript"
                                            onClick={() => setActiveTab('Transcript')}
                                            className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors duration-200 ${activeTab === 'Transcript'
                                                ? 'border-blue-500 text-blue-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                }`}
                                        >
                                            Transcript
                                        </button>
                                    ) : (
                                        ['Transcript', 'GP Letter', 'Case Notes'].map((tab) => (
                                            <button
                                                key={tab}
                                                onClick={() => {
                                                    if (tab === 'GP Letter' && transcriptions.length === 0) return;
                                                    if (tab === 'Case Notes' && transcriptions.length === 0) return;
                                                    console.log(`Switching to tab: ${tab}`);
                                                    if (tab === 'GP Letter') {
                                                        generateSummaryOpenAI();
                                                    }
                                                    if (tab === 'Case Notes') {
                                                        generateCaseNotesOpenAI();
                                                    }
                                                    setActiveTab(tab);
                                                }}
                                                className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors duration-200 ${activeTab === tab
                                                    ? 'border-blue-500 text-blue-600'
                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                    } ${transcriptions.length === 0 ? 'cursor-not-allowed opacity-50' : ''}`}
                                            >
                                                {tab}
                                            </button>
                                        ))
                                    )}
                                </nav>
                            </div>
                            <div className="p-4 max-h-[80vh] overflow-y-auto">
                                {activeTab === 'Transcript' &&
                                    <div>
                                        {transcriptions.map((transcription, index) => {
                                            const guestNumber = parseInt(transcription.speakerId.split('-')[1]);
                                            const position = guestNumber % 2 === 0 ? "right" : "left";
                                            return (
                                                <MessageBox
                                                    key={index}
                                                    position={position}
                                                    type="text"
                                                    text={transcription.text}
                                                    title={transcription.speakerId}
                                                    date={new Date()}
                                                    titleColor={transcription.isInterim ? "#666" : "#000"}
                                                />
                                            );
                                        })}
                                    </div>}
                                {activeTab === 'GP Letter' && <div>{summary && summary.trim() ? (<MarkdownTextPresenter markdown={summary} />) : "Generating GP letter. Please wait..."}</div>}
                                {activeTab === 'Case Notes' && <div>{caseNotes && caseNotes.trim() ? (<MarkdownTextPresenter markdown={caseNotes} />) : "Generating summary. Please wait..."}</div>}
                            </div>
                        </div>
                    </div>
                )
            )}

            {!isTranscripting && (
                <div className="flex-1 flex items-center justify-center">
                    <button
                        onClick={handleStartRecording}
                        className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-4 px-8 rounded-lg shadow-md transition duration-300 text-lg"
                    >
                        Start Session
                    </button>

                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-96 shadow-2xl">
                        <h2 className="text-2xl font-bold mb-6 text-gray-800">Select Audio Device</h2>
                        <form className="space-y-3 mb-6">
                            {devices.map((device) => (
                                <label
                                    key={device.deviceId}
                                    className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 
                                    hover:border-blue-500 hover:bg-blue-50 cursor-pointer transition-all duration-200"
                                >
                                    <input
                                        type="radio"
                                        name="audioDevice"
                                        value={device.deviceId}
                                        checked={tempSelectedDevice === device}
                                        onChange={() => handleDeviceSelect(device)}
                                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                    />
                                    <div className="p-2 bg-blue-100 rounded-full">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium text-gray-800">
                                            {device.label || `Microphone ${device.deviceId.slice(0, 5)}`}
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            Audio Input Device
                                        </p>
                                    </div>
                                </label>
                            ))}
                        </form>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 
                                hover:bg-gray-100 hover:border-gray-400 transition-all duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeviceSelection}
                                disabled={!tempSelectedDevice}
                                className={`px-4 py-2 rounded-lg text-white transition-all duration-200 
                                ${tempSelectedDevice 
                                    ? 'bg-blue-500 hover:bg-blue-600' 
                                    : 'bg-gray-300 cursor-not-allowed'}`}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {isReferenceModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-96 shadow-2xl">
                        <h2 className="text-2xl font-bold mb-6 text-gray-800">Please enter Appointment ID</h2>
                        <div className="flex flex-row items-center justify-center">
                            <h2 className="text-2xl font-bold mb-6">App-</h2>
                          
                            <form className="space-y-3 mb-6">
                                <input
                                type="text"
                                    value={patientReference}
                                    onChange={(e) => {
                                    const value = e.target.value;
                                    setPatientReference(value);
                                    // Add validation for null or empty value
                                    if (!value.trim()) {
                                        setReferenceError("Patient reference number is required");
                                    } else {
                                        setReferenceError("");
                                    }
                                }}
                                className={`w-32 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    referenceError ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="12345678"
                            />
                            </form>
                        </div>

                        <div className="flex items-center mb-4">
                            <p className="text-gray-500 text-sm break-words">
                                Please obtain verbal consent from the client before using Aud Scribe during the appointment session
                            </p>
                        </div>

                        <div className="flex items-start mb-4">
                            <input
                                type="checkbox"
                                id="consentCheckbox"
                                checked={consentChecked}
                                onChange={(e) => setConsentChecked(e.target.checked)}
                                className="mr-2 mt-[5px]" 
                            />
                            <label htmlFor="consentCheckbox" className="text-gray-700 leading-tight">
                                By proceeding, you confirm that consent has been granted by the client
                            </label>
                        </div>

                        
                       
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setIsReferenceModalOpen(false)}
                                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 
                                hover:bg-gray-100 hover:border-gray-400 transition-all duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmReferenceSelection}
                                disabled={!consentChecked}
                                className={`px-4 py-2 rounded-lg text-white transition-all duration-200 
                                    ${consentChecked ? "bg-blue-500 hover:bg-blue-600 cursor-pointer" : "bg-gray-400 cursor-not-allowed opacity-50"}`}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}







            

            {/* Add Confirmation Modal */}
            {showConfirmClose  && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-96 shadow-2xl">
                        <h2 className="text-2xl font-bold mb-4 text-gray-800">End Session</h2>
                        <p className="text-gray-600 mb-6">

                            Are you sure you want to end this transcription session? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowConfirmClose(false)}
                                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 
                                hover:bg-gray-100 hover:border-gray-400 transition-all duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmClose}
                                className="px-4 py-2 rounded-lg bg-red-500 text-white 
                                hover:bg-red-600 transition-all duration-200"
                            >
                                End Session
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {/* Add Confirmation Modal */}
            {showConfirmStop  && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-96 shadow-2xl">
                        <h2 className="text-2xl font-bold mb-4 text-gray-800">Stop Session</h2>
                        <p className="text-gray-600 mb-6">
                            
                            Are you sure you want to stop this transcription session? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowConfirmStop(false)}
                                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 
                                hover:bg-gray-100 hover:border-gray-400 transition-all duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmStop}
                                className="px-4 py-2 rounded-lg bg-red-500 text-white 
                                hover:bg-red-600 transition-all duration-200"
                            >
                                Stop Session
                            </button>
                        </div>
                    </div>
                </div>
            )}



            

            
        </div>
    );
};

export default TranscriptionComponent;